import { apiClient } from '@api'

/**
 * Dashboard Service
 *
 * API service for dashboard data fetching.
 * Provides methods for company and super admin dashboards.
 */
const dashboardService = {
	// ====================================================================
	// COMPANY DASHBOARD ENDPOINTS
	// ====================================================================

	/**
	 * Get company dashboard overview statistics
	 * @returns {Promise} API response with stats and task summary
	 */
	getCompanyOverview: () => {
		return apiClient.get('/dashboard/company/overview')
	},

	/**
	 * Get company projects for dashboard display
	 * @param {number} limit - Number of projects to fetch
	 * @returns {Promise} API response with projects array
	 */
	getCompanyProjects: (limit = 5) => {
		return apiClient.get(`/dashboard/company/projects?limit=${limit}`)
	},

	/**
	 * Get recent company activity
	 * @param {number} limit - Number of activities to fetch
	 * @returns {Promise} API response with activity array
	 */
	getCompanyActivity: (limit = 10) => {
		return apiClient.get(`/dashboard/company/activity?limit=${limit}`)
	},

	/**
	 * Get upcoming deadlines
	 * @param {number} days - Number of days to look ahead
	 * @returns {Promise} API response with deadlines array
	 */
	getDeadlines: (days = 7) => {
		return apiClient.get(`/dashboard/company/deadlines?days=${days}`)
	},

	/**
	 * Get team performance metrics
	 * @returns {Promise} API response with team performance data
	 */
	getTeamPerformance: () => {
		return apiClient.get('/dashboard/company/team-performance')
	},

	// ====================================================================
	// SUPER ADMIN DASHBOARD ENDPOINTS
	// ====================================================================

	/**
	 * Get super admin dashboard overview
	 * @returns {Promise} API response with system-wide stats
	 */
	getSuperAdminOverview: () => {
		return apiClient.get('/dashboard/super-admin/overview')
	},

	/**
	 * Get company growth data for charts
	 * @param {string} period - Time period ('6months' or '12months')
	 * @returns {Promise} API response with growth data
	 */
	getCompanyGrowth: (period = '12months') => {
		return apiClient.get(`/dashboard/super-admin/growth?period=${period}`)
	},

	/**
	 * Get system alerts for super admin
	 * @returns {Promise} API response with alerts data
	 */
	getAlerts: () => {
		return apiClient.get('/dashboard/super-admin/alerts')
	},

	/**
	 * Get companies overview for super admin
	 * @param {number} limit - Number of companies to fetch
	 * @param {string} sort - Sort field
	 * @returns {Promise} API response with companies array
	 */
	getCompaniesOverview: (limit = 10, sort = 'created_at') => {
		return apiClient.get(`/dashboard/super-admin/companies?limit=${limit}&sort=${sort}`)
	},

	/**
	 * Get package distribution data
	 * @returns {Promise} API response with package distribution
	 */
	getPackageDistribution: () => {
		return apiClient.get('/dashboard/super-admin/package-distribution')
	},

	// ====================================================================
	// ROLE-SPECIFIC ENDPOINTS
	// ====================================================================

	/**
	 * Get pending approvals for current user
	 * @returns {Promise} API response with pending approvals
	 */
	getMyApprovals: () => {
		return apiClient.get('/dashboard/my-approvals')
	},

	/**
	 * Get current user's tasks summary
	 * @returns {Promise} API response with task summary
	 */
	getMyTasksSummary: () => {
		return apiClient.get('/dashboard/my-tasks/summary')
	},

	/**
	 * Get pending decisions for executive
	 * @returns {Promise} API response with pending decisions
	 */
	getPendingDecisions: () => {
		return apiClient.get('/dashboard/pending-decisions')
	},

	/**
	 * Get projects needing attention for compliance manager
	 * @returns {Promise} API response with projects needing attention
	 */
	getProjectsNeedingAttention: () => {
		return apiClient.get('/dashboard/projects-needing-attention')
	},

	/**
	 * Get completion trend data
	 * @param {number} days - Number of days for trend
	 * @returns {Promise} API response with trend data
	 */
	getCompletionTrend: (days = 30) => {
		return apiClient.get(`/dashboard/completion-trend?days=${days}`)
	},

	// ====================================================================
	// COMPLIANCE MANAGER ENDPOINTS
	// ====================================================================

	/**
	 * Get compliance overview for manager dashboard
	 * @returns {Promise} API response with compliance stats
	 */
	getComplianceOverview: () => {
		return apiClient.get('/dashboard/compliance/overview')
	},

	/**
	 * Get framework progress data
	 * @returns {Promise} API response with framework progress
	 */
	getFrameworkProgress: () => {
		return apiClient.get('/dashboard/compliance/framework-progress')
	},

	/**
	 * Get audit readiness data
	 * @returns {Promise} API response with audit readiness metrics
	 */
	getAuditReadiness: () => {
		return apiClient.get('/dashboard/compliance/audit-readiness')
	},

	// ====================================================================
	// PROJECT MANAGER ENDPOINTS
	// ====================================================================

	/**
	 * Get project manager dashboard overview
	 * @returns {Promise} API response with PM stats
	 */
	getProjectManagerOverview: () => {
		return apiClient.get('/dashboard/pm/overview')
	},

	/**
	 * Get PM's active projects with progress
	 * @param {number} limit - Number of projects to fetch
	 * @returns {Promise} API response with projects array
	 */
	getPMProjects: (limit = 10) => {
		return apiClient.get(`/dashboard/pm/projects?limit=${limit}`)
	},

	/**
	 * Get resource allocation data
	 * @returns {Promise} API response with resource data
	 */
	getResourceAllocation: () => {
		return apiClient.get('/dashboard/pm/resources')
	},

	// ====================================================================
	// EXECUTIVE ENDPOINTS
	// ====================================================================

	/**
	 * Get executive dashboard overview
	 * @returns {Promise} API response with executive stats
	 */
	getExecutiveOverview: () => {
		return apiClient.get('/dashboard/executive/overview')
	},

	/**
	 * Get strategic metrics for executive
	 * @returns {Promise} API response with strategic metrics
	 */
	getStrategicMetrics: () => {
		return apiClient.get('/dashboard/executive/metrics')
	},

	/**
	 * Get risk summary for executive
	 * @returns {Promise} API response with risk data
	 */
	getRiskSummary: () => {
		return apiClient.get('/dashboard/executive/risk-summary')
	},

	// ====================================================================
	// EMPLOYEE ENDPOINTS
	// ====================================================================

	/**
	 * Get employee dashboard overview
	 * @returns {Promise} API response with employee stats
	 */
	getEmployeeOverview: () => {
		return apiClient.get('/dashboard/employee/overview')
	},

	/**
	 * Get assigned tasks for employee
	 * @param {string} status - Task status filter
	 * @param {number} limit - Number of tasks to fetch
	 * @returns {Promise} API response with tasks array
	 */
	getAssignedTasks: (status = 'all', limit = 10) => {
		return apiClient.get(`/dashboard/employee/tasks?status=${status}&limit=${limit}`)
	},

	/**
	 * Get employee's training progress
	 * @returns {Promise} API response with training data
	 */
	getTrainingProgress: () => {
		return apiClient.get('/dashboard/employee/training')
	},

	// ====================================================================
	// MANAGEMENT DASHBOARD ENDPOINTS
	// ====================================================================

	/**
	 * Get management compliance dashboard overview
	 * High-level, read-only dashboard for senior management
	 * @returns {Promise} API response with comprehensive compliance data
	 */
	getManagementOverview: () => {
		return apiClient.get('/dashboard/management/overview')
	}
}

export default dashboardService
